import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { TextWrapper, Wrapper } from "../../../constants/common/StyledGlobalConstants";
import FormTitle from "../common/FormTitle";
import PreferredAppointmentDate from "./PreferredAppointmentDate";
import AvailableDatesAndSlots from "./AvailableDatesAndSlots";
import moment from "moment";
import FetchSlotsProgress from "./FetchSlotsProgress";
import ConfirmedAppointmentDetails from "./ConfirmedAppointmentDetails";
import "./AppointmentTime.css";
import { useDispatch, useSelector } from "react-redux";
import { setRescheduleAppointmentDetails } from "../../../store/actions/schedulingportal/technicianAppointment";
import { BodyTextWrapper } from "../ViewAppointmentDetails";
import CPNotification from "../../../pages/Login/CPNotification";
import CurrentAppointmentDetails from "./CurrentAppointmentDetails";
import { DEVICE_BREAKPOINTS } from "../../../constants/appConstants";
import { chatDisplayHandler } from "../../../util/chatBotUtils";
import { SUPPORT_PHONE_NUMBERS } from "../../../constants/technicianAppointmentConstants";

const { TABLET } = DEVICE_BREAKPOINTS;

const AppointmentDateAndTime = ({ setIsNextDisabled, setSlotDetails }) => {
  const [preferredDate, setPreferredDate] = useState(null);
  const [vendorAvailableSlots, setVendorAvailableSlots] = useState(null);
  const [apptDateAndTime, setApptDateAndTime] = useState(null);
  const [showProgressBar, setShowProgressBar] = useState(false);
  const confirmationRef = useRef(null);
  const { isReschedule, rescheduleAppointmentDetails, jobDetails, isJobScheduleDateIsWithInThreeBusinessDays } = useSelector(
    (state) => state.TECHNICIANAPPOINTMENT
  );
  const { windowSize, isSourcewellGovernment } = useSelector((state) => state.ACCOUNT);
  const isMobile = windowSize.width < TABLET;
  const dispatch = useDispatch();
  const { scheduledDate, scheduledTime, jobDuration } = jobDetails || {};

  const formatTimeSlot = (scheduledTime, jobDuration) => {
    if (!scheduledTime) return "--";

    const startTime = moment(scheduledTime, "hh:mm A");
    const endTime = startTime.clone().add("minutes", jobDuration);
    return `${startTime.format("h:mm A")} - ${endTime.format("h:mm A")}`;
  };

  const formatDate = (scheduledDate) => {
    return scheduledDate ? moment(scheduledDate, "MM/DD/YYYY").format("MMMM D, YYYY") : "--";
  };

  const getRescheduleNotificationMessage = (isSourcewellGovernment) => {
    const phoneNumber = isSourcewellGovernment ? SUPPORT_PHONE_NUMBERS.GOVERNMENT : SUPPORT_PHONE_NUMBERS.COMMERCIAL;

    return <>Your original appointment date is too close to reschedule it online. Call Support on {phoneNumber} to reschedule.</>;
  };

  const currentAppointmentTimeSlot = formatTimeSlot(scheduledTime, jobDuration);
  const currentAppointmentDate = formatDate(scheduledDate);

  useEffect(() => {
    chatDisplayHandler(sessionStorage.getItem("currentPath"));
    //disable Next if appointment date and time is not selected
    if (apptDateAndTime) {
      setSlotDetails({
        ...apptDateAndTime,
      });
      setIsNextDisabled(false);
    } else {
      setIsNextDisabled(true);
    }
  }, [apptDateAndTime]);

  useEffect(() => {
    // store selected preferredDate and selected apptDateAndTime in redux
    if (isReschedule && rescheduleAppointmentDetails) {
      let appointmentDetails = {
        reason: rescheduleAppointmentDetails?.reason || "",
        rescheduleReasonCode: rescheduleAppointmentDetails?.rescheduleReasonCode || "",
        preferredDate: preferredDate?.toDateString() || "",
        apptDateAndTime: {
          ...apptDateAndTime,
        },
      };
      dispatch(setRescheduleAppointmentDetails({ ...appointmentDetails }));
    }
  }, [apptDateAndTime, preferredDate]);

  return (
    <>
      {isReschedule ? (
        <CurrentAppointmentDetails
          isJobScheduleDateIsWithInThreeBusinessDays={isJobScheduleDateIsWithInThreeBusinessDays}
          isMobile={isMobile}
          currentAppointmentDate={currentAppointmentDate}
          currentAppointmentTimeSlot={currentAppointmentTimeSlot}
        />
      ) : (
        <FormTitle title="Appointment time" />
      )}
      {isReschedule && isJobScheduleDateIsWithInThreeBusinessDays ? (
        <Wrapper marginTop="1.25rem">
          <CPNotification isOpen={true} notificationType="info" closeButton={false} height="auto">
            <Wrapper>
              <BodyTextWrapper>{getRescheduleNotificationMessage(isSourcewellGovernment)}</BodyTextWrapper>
            </Wrapper>
          </CPNotification>
        </Wrapper>
      ) : (
        <Wrapper marginTop="1.5rem" padding="1.5rem" border="1px solid #D8DADA" borderRadius="0.5rem" width="100%">
          <TextWrapper fontFamily="neue-haas-grotesk-display" fontWeight="700" fontSize="2rem" lineHeight="2.5rem">
            {isReschedule ? `New appointment` : ` Select a date and time`}
          </TextWrapper>
          <TextWrapper fontSize="0.875rem" fontWeight="400" marginTop="1rem !important">
            Select your preferred date and time slot. Appointments must be scheduled at least <b>48 hours</b> in advance.
          </TextWrapper>
          <PreferredAppointmentDate
            preferredDate={preferredDate}
            setPreferredDate={setPreferredDate}
            setVendorAvailableSlots={setVendorAvailableSlots}
            vendorAvailableSlots={vendorAvailableSlots}
            setApptTimeDateAndTime={setApptDateAndTime}
            showProgressBar={showProgressBar}
            setShowProgressBar={setShowProgressBar}
          />
          {vendorAvailableSlots ? (
            <AvailableDatesAndSlots
              key={preferredDate}
              preferredDate={preferredDate}
              apptDateAndTime={apptDateAndTime}
              vendorAvailableSlots={vendorAvailableSlots}
              showProgressBar={showProgressBar}
              setApptTimeDateAndTime={setApptDateAndTime}
              scheduledDate={isReschedule ? currentAppointmentDate : null}
              scheduledTime={isReschedule ? currentAppointmentTimeSlot : null}
            />
          ) : (
            <>{showProgressBar && <FetchSlotsProgress />}</>
          )}
          {apptDateAndTime && <ConfirmedAppointmentDetails apptDateAndTime={apptDateAndTime} confirmationRef={confirmationRef} />}
        </Wrapper>
      )}
    </>
  );
};
AppointmentDateAndTime.propTypes = {
  setIsNextDisabled: PropTypes.func.isRequired,
  setSlotDetails: PropTypes.func.isRequired,
};

export default AppointmentDateAndTime;
